import { EventEmitter, Injectable, Injector } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { BehaviorSubject, Subject, Observable, Subscription } from 'rxjs';
import { take, takeUntil, map } from 'rxjs/operators';
import { UnitService } from '../helper/anessa-library/anessa-library/projects/unit-library/src/public-api';
import { CustomSnackbarComponent } from '../helper/custom-snackbar/custom-snackbar.component';
import { ConfiguredToastrService } from '../helper/toastr/configured-toastr.service';
import { UnitValueValidation } from '../helper/validation/validation-sub-modules/unit-value.validation';
import { Feedstock } from '../models/project/feedstock/feedstock.model';
import { ProjectFinancial } from '../models/project/financial/financial.model';
import { GhgEmissions } from '../models/project/ghg-emission/ghg-emissions.model';
import { Fuel } from '../models/project/plant-specifications/fuel.model';
import { PlantSpecifications } from '../models/project/plant-specifications/plant-specifications.model';
import { Product } from '../models/project/plant-specifications/product.model';
import { ProjectInfo } from '../models/project/project-info.model';
import { Project, ProjectInterface } from '../models/project/project.model';
import { ScenarioComparison } from '../models/results/scenario-comparison/scenario-comparison.model';
import { UnitValue } from '../models/data-structures/unit-value.model';
import Utils from '../util';
import { CommunicationService } from './communication.service';
import { Sensitivity } from '../models/results/sensitivity/sensitivity.model';
import { SimulationProgress } from '../models/results/sim-progress/simulation-progress.model';
import { SensitivityItem } from '../models/results/sensitivity/sensitivity-item.model';
import { Permissions } from '../models/user.model';
import moment from 'moment';
import { AnessaTranslationService } from '../helper/translation/anessa-translation.service';
import { AdditionalProduct } from '../models/project/plant-specifications/additional-product.model';
import { Loan } from '../models/project/financial/financial-investment/loan.model';
import { Alert } from '../models/helper/alerts-menu.model';
import { Results } from '../models/results/results.model';
import { SensitivityResultsInterface } from '../models/results/sensitivity/sensitivity-result.model';
import { AdaResultSet, AdaResultSetInterface } from '../models/results/result-set.model';
import { ProgressMessage } from '../models/data-structures/progress-message.model';



@Injectable()
export class ProjectCommunicationService {
  project: Project;
  projectIsInputForm = false;
  projectSaveToDatabaseDisabled = false;
  projectDataLoaded = false;
  projectLoadedChange: Subject<boolean> = new Subject<boolean>();
  projectFullyLoadedNotifier: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  projectReloadedNotifier = new EventEmitter<boolean>();
  projectLoadedValue;
  projectResultsNotifier: Subject<boolean> = new Subject<boolean>();
  projectSavedNotifier: Subject<boolean> = new Subject<boolean>();
  projectNamesNotifier: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  projectInputDraftSaveNotifier = new EventEmitter();
  projectTitles: { id: string, title: string }[] = [];
  translationService: AnessaTranslationService;
  sampleList: any;
  // sidebar toggles for printing
  displayNavSidebar = true;
  displayProgressSidebar = true;

  // results
  simulationStartedNotifier = new EventEmitter<void>();
  simulationCompleteNotifier = new EventEmitter<boolean>();
  private nextPath = '';
  private resultNames: { starred: boolean, timestamp: number, name: string }[] = [];
  private currentResults?: {
    timestamp: number,
    results: Results
  } = null;

  // sensitivity Solver
  resultsLoadedNotifier = new BehaviorSubject<boolean>(false);
  currentResultSet = 'latest';
  progressSubscription?: Subscription;

  constructor(
    private communicationService: CommunicationService,
    private unitService: UnitService,
    private router: Router,
    private snackService: MatSnackBar,
    private toastrService: ConfiguredToastrService,
    private injector: Injector
  ) {
    setTimeout(() => this.translationService = this.injector.get(AnessaTranslationService));
    this.project = new Project();
    this.projectResultsNotifier.next(false);

    this.progressSubscription = this.communicationService.progressNotifier
      .subscribe(msg => {
        if (!msg) return;
        if (msg.type === 'sensitivity') {
          this.handleSensitivityProgress(msg);
        } else if (msg.type === 'scenarios') {
          this.handleScenariosProgress(msg);
        }
    });
  }

  private handleSensitivityProgress(msg: ProgressMessage) {
    if (msg.simProgress === 1) {
      this.snackService.dismiss();
      this.toastrService.success(this.translationService.get('SERVICE.PROJECT_COMM.SENSITIVITY_SAVED'));
      this.reloadProject();
    } else if (msg.simProgress === -1) {
      this.snackService.dismiss();
      this.toastrService.error(this.translationService.get('SERVICE.PROJECT_COMM.SENSITIVITY_ANALYSIS_ERROR'));
    } else {
      // more progress notifications, percent done can go here if we want it
    }
  }

  private handleScenariosProgress(msg: ProgressMessage) {
    if (msg.simProgress === 1 && !msg.message) {
      this.snackService.dismiss();
      const message = this.translationService.get('SCENARIO_COMPARISON.SERVICE.SCENARIO_COMPLETE_ALL');
      this.toastrService.success(message);
    } else if (msg.simProgress === -1) {
      const message = this.translationService.get('SCENARIO_COMPARISON.SERVICE.SCENARIO_SIM_FAIL')
        .replace('{name}', msg.message);
      this.toastrService.error(message);
      this.snackService.dismiss();
    } else if (msg.message) {
      // more progress notifications, percent done can go here if we want it
      const message = this.translationService.get('SCENARIO_COMPARISON.SERVICE.SCENARIO_SIM_COMPLETE')
        .replace('{name}', msg.message);
      this.toastrService.success(message)
    }
  }

  // List of units that will be checked when searching for project currencies
  currencyUnits: string[] = [
    '000000000000000000000020',  // Currency
    '000000000000000000000022',  // CPI
  ];

  /**
   * Recursive function to traverse and object and find any currency UnitValues.
   * Only unit values that contain a non-null value will be counted towards the
   * list of found currencies
   */
  private findCurrenciesRec(obj: any, currencies: string[], path: string[]) {
    if (!(obj instanceof Object)) {
      return;
    }

    if (obj instanceof UnitValue) {
      const unit = this.unitService.getUnitById(obj.unit);
      if (unit && this.currencyUnits.includes(unit.unitType)) {
        // Ignore any UnitValues with no value to ignore LCFS/RIN always in USD
        if (obj.value != null && !currencies.includes(unit.displayName.slice(0, 3)) && !unit.displayName.includes('%')) {
          // Only take the first three characters so that CAD and CAD/year are treated as the same currency
          currencies.push(unit.displayName.slice(0, 3));
        }
      }
      return;
    }

    // Traverse project recursively
    if (Array.isArray(obj)) {
      for (const item of obj) {
        this.findCurrenciesRec(item, currencies, [...path, String(obj.indexOf(item))]);
      }
    } else {
      for (const item of Object.keys(obj)) {
        this.findCurrenciesRec(obj[item], currencies, [...path, item]);
      }
    }
  }

  /**
   * Find which currencies are used in the project
   * @returns a list containing found currency names
   */
  getProjectCurrencies() {
    const currencies = [];
    this.findCurrenciesRec(this.project, currencies, []);
    return currencies;
  }

  isSingleCurrencyUsed() {
    const currencies = this.getProjectCurrencies();
    return currencies.length === 1;
  }

  getBaseCurrencyId() {
    if (this.isSingleCurrencyUsed()) {
      return this.unitService.getUnitByName(this.getProjectCurrencies()[0]).id;
    }
    return '000000000000000000002004';
  }

  /**
   * Checks whether a single currency (Currency and CPI are both checked) is
   * used exclusively throughout the project.
   *price.value
   *
   * If multiple currencies are found, the flag will not be set and CAD will be
   * set as the base currency
   */
  checkCurrenciesUsed() {
    this.project.projectInfo.singleCurrencyUsed = this.isSingleCurrencyUsed();
    this.project.projectInfo.baseCurrencyId = this.getBaseCurrencyId();
  }

  getProjectNames() {
    this.communicationService.getAllRequest('/wsasapi/project/getnames')
      .pipe(take(1)).subscribe(
      data => {
        this.projectTitles = data['obj'];
        this.projectNamesNotifier.next(true);
      }
    );
  }

  /**
   * Checks if a project title is already in use by another proejct
   *
   * @param title project title to check if already exists
   * @returns true if the title is unique of false if it is a duplicate
   */
  checkUniqueProjectTitle(title: string) {
    title = title.trim();
    if (title === '') {
      this.toastrService.error(this.translationService.get('SERVICE.PROJECT_COMM.EMPTY_NAME_ERROR'));
      return false;
    }
    setTimeout(() => this.getProjectNames(), 0);
    for (let i = 0; i < this.projectTitles.length; i++) {
      if (this.projectTitles[i].title.toLowerCase() === title.toLowerCase()) {
        this.toastrService.error(this.translationService.get('SERVICE.PROJECT_COMM.DUPE_NAME_ERROR'));
        return false;
      }
    }
    return true;
  }

  getProjectId() {
    if (this.project) {
      return this.project._id;
    }
    return null;
  }

  getProject(): Project {
    if (this.project) {
      return Project.deepCopy(this.project);
    }
  }

  setProject(project: Project) {
    if (project === null || project === undefined) throw Error('cannot set null/undefined project');
    this.project = new Project(project);
    localStorage.setItem('projectID', this.project._id);
    this.setProjectLoaded(true);
  }

  getProjectLoaded() {
    return this.projectDataLoaded;
  }

  setProjectLoaded(loaded: boolean) {
    this.projectDataLoaded = loaded;
    this.checkFullyLoaded();
  }

  getProjectSavedNotifier(): Subject<boolean> {
    return this.projectSavedNotifier;
  }

  checkFullyLoaded() {
    if (this.projectDataLoaded) {
      this.projectFullyLoadedNotifier.next(true);
      this.projectLoadedChange.next(this.projectDataLoaded);
      this.projectLoadedValue = this.projectDataLoaded;

      // if rerouting was requested, perform it
      if (this.nextPath !== '') {
        this.router.navigateByUrl(this.nextPath);
        this.nextPath = '';
      }
    } else {
      this.projectFullyLoadedNotifier.next(false);
    }
  }

  deleteProject() {
    this.communicationService.deleteRequest(this.project._id, '/wsasapi/project')
      .pipe(take(1)).subscribe(
      data => {
        this.toastrService.success(this.translationService.get('SERVICE.PROJECT_COMM.DELETED'));
        this.unloadProject();
        this.communicationService.fetchReachedProjectLimit();
      }
    );
  }

  setProjectInfo(info: ProjectInfo, results?: any, deleteScenarioResults = true, skipSave = false) {
    if (!this.project) {
      this.project = new Project();
    }
    this.project.projectInfo = info;
    if (!skipSave) {
      this.saveProject(results, deleteScenarioResults);
    }
  }

  getProjectDefaults() {
    if (this.project && this.project.projectInfo) {
      return this.project.projectInfo.defaults;
    }
  }


  getDesiredXValue(slope: number, gradient: number, expectedY: number , xvals: any , yvals: any) {
    return this.communicationService.calculateDesiredX(slope, gradient, expectedY, xvals, yvals);
  }

  getPrecision() {
    if (this.project && this.project.projectInfo && this.project.projectInfo.defaults) {
      const precision = this.project.projectInfo.defaults.decimalPrecision;
      if (precision && new UnitValueValidation(precision).isValid()) {
        return this.project.projectInfo.defaults.decimalPrecision.value;
      }
    }
    return 2;
  }

  getProjectInfo(): ProjectInfo {
    if (this.project) {
      return ProjectInfo.deepCopy(this.project.projectInfo);
    }
  }

  simulateProject(postPack: any) {
    if (postPack == null || postPack.id == null) {
      return;
    }
    this.snackService
      .openFromComponent(CustomSnackbarComponent,
        {
          data: {
            message: this.translationService.get('SERVICE.PROJECT_COMM.SIMULATING'),
            id: 'project-simulating_snackbar'
          },
          panelClass: ['anessa-snack-bar-container'],
        }
      );
    this.simulationStartedNotifier.next();
    this.communicationService.postRequest(postPack, '/wsasapi/project/simulate')
      .pipe(take(1)).subscribe(
        data => {
          this.snackService.dismiss();
          if (data['obj']) {
            const iProject: ProjectInterface = JSON.parse(JSON.stringify(data['obj']));
            this.project = new Project(iProject);
            if (data['obj'].results) {
              this.projectResultsNotifier.next(true);
            } else {
              // sim finished no results
              this.simulationCompleteNotifier.emit(false);
              throw Error(this.translationService.get('SERVICE.PROJECT_COMM.NO_RESULTS'));
            }
            this.loadResults();
            this.toastrService.success(this.translationService.get('SERVICE.PROJECT_COMM.SIM_COMPLETE'));
            // sim finished success
            this.simulationCompleteNotifier.emit(true);
          } else {
            console.error('simulation didn\'t return the expected response');
            // sim finished but failed
            this.simulationCompleteNotifier.emit(false);
          }
        }
      );
  }

  getFeedstocks() {
    if (this.project) {
      return Feedstock.arrayDeepCopy(this.project.feedstocks);
    }
    throw Error(this.translationService.get('SERVICE.PROJECT_COMM.CANT_GET_FEEDSTOCKS'));
  }

  saveFeedstocks(feedstocks: Feedstock[], resetCapexOpex: boolean) {
    if (this.project) {
      this.project.feedstocks = feedstocks;
      if (resetCapexOpex) {
        this.resetCapexOpex();
      }
      this.updatePlantSpecDensityRelation();
      this.updatePlantSpecCodRelation();
      this.saveProject();
      return true;
    }
    throw Error(this.translationService.get('SERVICE.PROJECT_COMM.CANT_SAVE_FEEDSTOCKS'));
  }

  getProjectPlantSpecifications(): PlantSpecifications {
    if (this.project) {
      return PlantSpecifications.deepCopy(this.project.plantSpecifications);
    }
    throw Error('null plant');
  }

  setProjectPlantSpecifications(plantSpec: PlantSpecifications, resetCapexOpex = true, skipSave = false) {
    this.project.plantSpecifications = plantSpec;
    if (resetCapexOpex) {
      this.resetCapexOpex();
    }
    this.updateDensityEnabled();
    this.updateCodEnabled();
    // used for ghg so the project isn't being saved twice
    if (!skipSave) {
      this.saveProject();
    }
  }

  getGeneratedProductItems() {
    if (this.project && this.project.plantSpecifications && this.project.plantSpecifications.generatedProducts) {
      return Product.arrayDeepCopy(this.project.plantSpecifications.generatedProducts);
    } else {
      return null;
    }
  }

  getAdditionalProductItems() {
    if (this.project && this.project.plantSpecifications && this.project.plantSpecifications.additionalProducts) {
      return AdditionalProduct.arrayDeepCopy(this.project.plantSpecifications.additionalProducts);
    } else {
      return null;
    }
  }

  getGeneratedProduct(index: number) {
    if (this.project && this.project.plantSpecifications && this.project.plantSpecifications.generatedProducts && index > -1
      && index < this.project.plantSpecifications.generatedProducts.length) {
      return Product.deepCopy(this.project.plantSpecifications.generatedProducts[index]);
    }
  }

  getGeneratedProductIndexByType(type: string) {
    if (this.project && this.project.plantSpecifications) {
      for (const genProd of this.project.plantSpecifications.generatedProducts) {
        if (genProd.name === type) {
          return this.project.plantSpecifications.generatedProducts.indexOf(genProd);
        }
      }
    }
  }

  saveGeneratedProduct(product: Product, index: number) {
    if (index > -1 && index < this.project.plantSpecifications.generatedProducts.length) {
      this.project.plantSpecifications.generatedProducts[index] = product;
      this.saveProject();
    }
  }

  saveGeneratedProducts(products: Product[]) {
    this.project.plantSpecifications.generatedProducts = products;
    this.saveProject();
  }

  saveAdditionalProduct(product: AdditionalProduct, index: number) {
    if (index > -1 && index < this.project.plantSpecifications.additionalProducts.length) {
      this.project.plantSpecifications.additionalProducts[index] = product;
      this.saveProject();
    }
  }

  getConsumedFuelItems() {
    return Utils.deepCopy(this.project.plantSpecifications.consumedFuels);
  }

  getConsumedFuel(index: number) {
    if (index < 0) {
      return null;
    }
    if (this.project && this.project.plantSpecifications && this.project.plantSpecifications.consumedFuels &&
      index < this.project.plantSpecifications.consumedFuels.length) {
      return Fuel.deepCopy(this.project.plantSpecifications.consumedFuels[index]);
    }
    return null;
  }


  getConsumedFuelIndexByType(type: string) {
    if (this.project && this.project.plantSpecifications) {
      for (const consumedFuel of this.project.plantSpecifications.consumedFuels) {
        if (consumedFuel.name === type) {
          return this.project.plantSpecifications.consumedFuels.indexOf(consumedFuel);
        }
      }
    }
  }


  getAdditionalProductsIndexByType(type: string) {
    if (this.project && this.project.plantSpecifications) {
      for (const additionalProducts of this.project.plantSpecifications.additionalProducts) {
        if (additionalProducts.name === type) {
          return this.project.plantSpecifications.additionalProducts.indexOf(additionalProducts);
        }
      }
    }
  }


  saveConsumedFuel(fuel: Fuel, index: number) {
    if (this.project.plantSpecifications) {
      if (index > -1 && index < this.project.plantSpecifications.consumedFuels.length) {
        this.project.plantSpecifications.consumedFuels[index] = fuel;
        this.saveProject();
      }
    }
  }

  getFinancial() {
    return ProjectFinancial.deepCopy(this.project.financial);
  }

  getLoans(): Loan[] {
    return this.project?.financial?.investment?.loans;
  }

  getNonForgivenLoans(): Loan[] {
    const resultLoans: Loan[] = [];
    if (!this.project?.financial?.investment?.loans) return resultLoans;
    for (const loan of this.project.financial.investment.loans) {
      if (!loan.isForgiven()) resultLoans.push(loan);
    }
    return resultLoans;
  }

  saveFinancial(fin: ProjectFinancial) {
    this.project.financial = fin;
    this.saveProject();
  }

  resetCapexOpex() {
    if (this.project && this.project.financial) {
      const removedCapexItems = this.removeEstimatedCapexItems();
      const removedOpexItems = this.removeEstimatedOpexItems();
      const showToastr = (removedCapexItems || removedOpexItems);
      if (showToastr) {
        this.toastrService.info(
          this.translationService.get('SERVICE.PROJECT_COMM.CAPEX_OPEX_REMOVED'),
          this.translationService.get('SERVICE.PROJECT_COMM.CAPEX_OPEX_UPDATED')
        );
      }
    }
  }

  /**
   * Update the density enabled status of all feedstocks based the setup of the plant specification module.
   */
  updateDensityEnabled() {
    if (this.project && this.project.plantSpecifications) {
      if (this.project.plantSpecifications.isDryBatch()) {
        if (this.project.feedstocks) {
          let showMessage = false;
          for (const feedstock of this.project.feedstocks) {
            if (!feedstock.characteristics) throw Error(this.translationService.get('SERVICE.PROJECT_COMM.CHARACTERISTICS_UNAVAILABLE'));
            if (!feedstock.characteristics.densityEnabled) showMessage = true;
            feedstock.characteristics.characteristicsEnabled = true;
            feedstock.characteristics.densityEnabled = true;
          }
          // Need to save so that project is updated in other input form components
          if (this.isProjectInput()) {
            this.saveProject();
          }
          if (showMessage) {
            this.toastrService.info(
              this.translationService.get('SERVICE.PROJECT_COMM.DENSITY_ENABLED_FOR_ALL'),
              this.translationService.get('SERVICE.PROJECT_COMM.DENSITY_ENABLED')
            );
          }
        }
      }
    }
  }

  /**
   * Updates the status for COD enabled for each feedstock based on the unit selected for the digester period in
   * the plant spec module.
   */
  updateCodEnabled() {
    if (this.project && this.project.plantSpecifications) {
      if (this.project.plantSpecifications.digesterPeriodUnitSelected()) {
        if (this.project.feedstocks) {
          const enabled = this.project.plantSpecifications.digesterPeriodHasOlrCodUnit();
          for (const feedstock of this.project.feedstocks) {
            if (!feedstock.characteristics) throw Error(this.translationService.get('SERVICE.PROJECT_COMM.CHARACTERISTICS_UNAVAILABLE'));
            feedstock.characteristics.codEnabled = enabled;
          }
        }
      }
    }
  }

  /**
   * Update the plant specification module setup to reflect changes made to feedstocks.
   * If density has been disable while dry batch was selected, the digester type will be removed.
   */
  updatePlantSpecDensityRelation() {
    if (this.project && this.project.feedstocks && this.project.plantSpecifications) {
      if (this.project.plantSpecifications.isDryBatch()) {
        if (this.project.feedstocks.length > 0 && this.project.feedstocks[0].characteristics) {
          if (!this.project.feedstocks[0].characteristics.densityEnabled) {
            if (this.project.plantSpecifications.isDryBatch()) {
              this.project.plantSpecifications.selectedDigester = null;
            }
          }
        }
      }
    }
  }

  /**
   * Checks if cod is enabled and removes any non COD unit from the digester period in the plant spec module.
   */
  updatePlantSpecCodRelation() {
    if (this.project && this.project.feedstocks && this.project.plantSpecifications) {
      if (this.project.plantSpecifications.digesterPeriodUnitSelected()) {
        // at this point all feedstock have had cod enabled disabled for all
        if (this.project.feedstocks.length > 0 && this.project.feedstocks[0].characteristics) {
          if (!this.project.feedstocks[0].characteristics.codEnabled) {
            if (this.project.plantSpecifications.digesterPeriodHasOlrCodUnit()) {
              if (this.project.plantSpecifications.periodOLR) {
                this.project.plantSpecifications.periodOLR.unit = null;
              }
            }
          }
        }
      }
    }
  }

  getProjectGhgEmissions() {
    if (this.project) {
      return GhgEmissions.deepCopy(this.project.ghgEmissions);
    }
  }

  saveGhgEmissions(ghgEmi: GhgEmissions, plantSpec: PlantSpecifications) {
    if (this.project) {
      this.setProjectPlantSpecifications(plantSpec, false, true);
      this.project.ghgEmissions = ghgEmi;
      this.saveProject();
    }
  }

  getSensitivity() {
    return Sensitivity.deepCopy(this.project.sensitivity);
  }

  /**
   * Save the sensitivity
   * @param sensitivity
   */
  saveSensitivity(sensitivity: Sensitivity) {
    if (!this.project.accountID) {
      const accountID = localStorage.getItem('accountID');
      if (accountID) {
        this.project.accountID = accountID;
      }
    }
    this.snackService
      .openFromComponent(CustomSnackbarComponent,
        {
          data: {
            message: this.translationService.get('SERVICE.PROJECT_COMM.SENS_IN_PROGRESS'),
            id: 'sensitivity-in-progress_snackbar'
          },
          panelClass: ['anessa-snack-bar-container'],
        }
      );
    this.project.sensitivity = new Sensitivity(sensitivity);
    this.saveProject(null, false);
    this.communicationService.performSensitivityAnalysisMetricsSave();
    this.projectSavedNotifier.pipe(take(1))
      .subscribe({
        next: saved => {
          if (saved) {
            this.communicationService.preformSensitivityAnalysis().pipe(take(1))
              .subscribe({
                next: () => {
                  this.toastrService.success(this.translationService.get('SERVICE.PROJECT_COMM.SENSITIVITY_STARTED'));
                },
                error: error => {
                  this.snackService.dismiss();
                  console.error('The server did not respond: ', error);
                  this.toastrService.error(this.translationService.get('SERVICE.PROJECT_COMM.SENSITIVITY_ANALYSIS_ERROR'));
                }
              });
          }
        },
        error: error => {
          this.snackService.dismiss();
          console.error('Error saving project: ', error);
          this.toastrService.error(this.translationService.get('SERVICE.PROJECT_COMM.PROJECT_SAVED_ERROR'));
        }
      });
  }

  fetchSensResults() {
    if (!this.project || !this.project.sensitivity || !this.project.sensitivity.inputs) {
      Error('no sensitivity data');
      return;
    }
    if (!this.project.sensitivity.hasInputs()) {
      this.toastrService.warning(
        this.translationService.get('SENSITIVITY.ERRORS.ACTUAL.PLEASE_ADD_INPUTS'),
        this.translationService.get('SENSITIVITY.ERRORS.ACTUAL.NO_INPUTS')
      );
      return;
    }
    if (!this.project.sensitivity.inputsAreValid()) {
      this.toastrService.warning(this.translationService.get('SENSITIVITY.ERRORS.ACTUAL.INVALID_INPUTS'));
      return;
    }
    return this.communicationService.postRequest(
      { id: this.project._id },
      '/wsasapi/sensitivity/getResults'
    ) as Observable<{ obj: SensitivityResultsInterface }>;
  }


  calculateLinearSolver(slope: number, gradient: number) {

  }

  getSensitivityInputs() {
    if (this.project && this.project.sensitivity && this.project.sensitivity.inputs) {
      return SensitivityItem.arrayDeepCopy(this.project.sensitivity.inputs);
    }
  }

  getSimProgress(): SimulationProgress | undefined {
    if (this.project) {
      return this.project.simProgress;
    }
  }

  setSimProgress(simProgress: SimulationProgress) {
    if (this.project) {
      this.project.simProgress = simProgress;
    }
  }

  deleteScenarios() {
    if (this.project && this.project.sensitivity) {
      this.project.scenarioComparison.scenarios = [];
    }
  }

  getHistoryNames() {
    const projectID = localStorage.getItem('projectID');
    return this.communicationService.getAllRequest('/wsasapi/results/getResultHistory/' + projectID);
  }

  loadHistorySet(timestamp: string) {
    const projectID = localStorage.getItem('projectID');
    return this.communicationService.getRequest(timestamp, '/wsasapi/results/getResultFromHist/' + projectID);
  }

  loadResults(targetPath?: String, disableErrorMessages?: boolean) {
    this.getHistoryNames().pipe(take(1)).subscribe(
      data => {
        this.resultNames = data['obj'].historyNames;
        if (localStorage.getItem('projectID') != null) {
          if (!disableErrorMessages && data['obj'] && data['obj'].historyNames && !data['obj'].historyNames.length) {
            // don't show warning if the user account has the dark release flag
            if (!Utils.hasInputListAccess()) {
              this.toastrService.warning(
                this.translationService.get('RESULTS.ERRORS.PLEASE_SIMULATE'),
                this.translationService.get('RESULTS.ERRORS.NO_RESULTS')
              );
            }
          }
          this.loadRequiredResultSet(targetPath, disableErrorMessages);
        }
      });
  }

  /**
   * For loop to check existence of keys in array
   * @param obj
   * @param args
   * @private
   */
  private checkNested(obj, args) {
    for (let i = 0; i < args.length; i++) {
      if (!obj || !obj.hasOwnProperty(args[i])) {
        return false;
      }
      obj = obj[args[i]];
    }
    return true;
  }

  loadRequiredResultSet(targetPath?: String, disableErrorMessages?: boolean) {
    if (this.resultNames && this.resultNames.length > 0) {
      this.promiseResultsSet(this.currentResultSet, disableErrorMessages).then((results: any) => {
        this.currentResults = results;
        this.currentResults.results = new Results(results.results);
        if (this.checkNested(this.currentResults.results, ['RESULTS.DATA.FINANCIAL', 'RESULTS.DATA.CASH_FLOW', 'RESULTS.DATA.CASHFLOWS.PPC'])) {
          delete this.currentResults.results['RESULTS.DATA.FINANCIAL']['RESULTS.DATA.CASH_FLOW']['RESULTS.DATA.CASHFLOWS.PPC'];
        }
        if (!results) {
          this.resultsLoadedNotifier.next(false);
        }
        let setName = this.getFullResultName(this.currentResultSet);
        setName = this.timestampToDateString(setName);
        if (this.currentResultSet === 'latest') {
          setName = this.translationService.get('RESULTS.HISTORY_PICKER.LATEST') + ' (' + setName + ')';
        } else if (results['resultsSetName']) {
          setName = results['resultsSetName'];
        }
        if ((targetPath !== undefined && !targetPath.includes('results')) || targetPath === undefined) {
          this.toastrService.success(this.translationService.get('SERVICE.PROJECT_COMM.RESULTS_LOADED') + ': ' + setName);
        }
        this.resultsLoadedNotifier.next(true);
      });
    }
  }

  fetchLatestResultSet() {
    const projectID = this.project._id;
    const timestamp = this.getLatestName();
    return (this.communicationService.getRequest(
      timestamp, '/wsasapi/results/getResultFromHist/' + projectID
    ) as Observable<{ obj: AdaResultSetInterface }>)
      .pipe(map(res => new AdaResultSet(res.obj))
      );
  }

  promiseResultsSet(timeStamp: string | number, disableErrorMessages?: boolean) {
    const set = this.getFullResultName('' + timeStamp);
    return new Promise((fulfill, reject) => {
      this.loadHistorySet('' + set).pipe(take(1)).subscribe(
        results => {
          fulfill(results['obj']);
        },
        error => {
          if (error.error.message === 'requested resource could not be found') {
            if (!disableErrorMessages) {
              this.toastrService.error(this.translationService.get('SERVICE.PROJECT_COMM.NO_RESULTS_FOUND'));
            }
          } else {
            throw error;
          }
          return null;
        }
      );
    });
  }

  selectResult(selection: string | number) {
    this.currentResultSet = '' + selection;
    if (localStorage.getItem('projectID') != null) {
      this.loadRequiredResultSet();
    }
  }

  getCurrentResultsName() {
    return this.currentResultSet;
  }

  markResultFavourite(timestamp: number, b: boolean) {
    return this.communicationService.postRequest({ timestamp: timestamp, value: b },
      '/wsasapi/results/setFavouriteResult/' + this.getProjectId()).pipe(take(1)).subscribe(
        data => {
          if (data) {
            this.toastrService.success(this.translationService.get('SERVICE.PROJECT_COMM.FAVORITES_CHANGED'));
            this.loadResults();
          }
        }
      );
  }

  promiseRenameResult(resultTimestamp: number, newName: string) {
    return new Promise((fulfill, reject) => {
      this.communicationService.postRequest({ timestamp: resultTimestamp, newName: newName },
        '/wsasapi/results/renameResult/' + this.getProjectId()).pipe(take(1)).subscribe(
          data => {
            if (data) {
              this.toastrService.success(this.translationService.get('SERVICE.PROJECT_COMM.RESULT_RENAMED'));
              if (this.project && this.project.projectInfo && this.project.projectInfo.isApprovedProject !== false) {
                this.getHistoryNames().pipe(take(1)).subscribe(
                  data => {
                    this.resultNames = data['obj'].historyNames;
                    fulfill(this.resultNames);
                  }
                );
              }
            }
          }
        );
    });
  }

  private getFullResultName(timeStamp?: string) {
    if (!timeStamp) {
      timeStamp = '' + this.currentResultSet;
    }
    let set = '';
    if (timeStamp === 'latest') {
      set = '' + this.getLatestName();
    } else {
      set = timeStamp;
    }
    return set;
  }

  public timestampToDateString(stamp) {
    return moment(stamp, 'x').format('YYYY-MM-DD HH:mm');
  }

  private getLatestName(): string {
    let result = 0;

    if (!this.resultNames || !(this.resultNames.length > 0)) {
      return String(result);
    }

    for (const set of this.resultNames) {
      if (set) {
        const time = set.timestamp;
        if (time > result) {
          result = time;
        }
      }
    }
    return String(result);
  }

  getResultProjectState() {
    if (this.project && this.currentResults && this.currentResults['project-state']) {
      return new Project(this.currentResults['project-state']);
    }
    return null;
  }

  getResultTimestamp() {
    if (this.project && this.currentResults && this.currentResults.timestamp) {
      return this.currentResults.timestamp;
    }
  }

  getResults() {
    if (this.project && this.currentResults && this.currentResults.results) {
      return this.currentResults.results;
    }
  }

  getResultsProject() {
    if (this.project && this.currentResults && this.currentResults) {
      return this.currentResults.results;
    }
  }

  getResultNames() {
    return this.resultNames;
  }

  /**
   * Get the result id for the result set used for the scenario results simulation
   */
  getScenarioComparisonResultId() {
    if (this.project && this.project.scenarioComparison) {
      return this.project.scenarioComparison.resultHistoryID;
    }
  }

  getScenarioComparison() {
    if (this.project && this.project.scenarioComparison) {
      return this.project.scenarioComparison.deepCopy();
    }
  }

  saveScenarioComparison(sceComp: ScenarioComparison) {
    if (this.project && this.project.scenarioComparison) {
      this.project.scenarioComparison = sceComp;
      this.saveProject();
    }
  }

  disableProjectSaveToDatabase() {
    this.projectSaveToDatabaseDisabled = true;
  }

  enableProjectSaveToDatabase() {
    this.projectSaveToDatabaseDisabled = false;
  }

  setIsProjectInput(isProjectInput: boolean) {
    this.projectIsInputForm = isProjectInput;
  }

  /**
   * If the save to database is disabled then we are in a project input form
   */
  isProjectInput() {
    return this.projectIsInputForm;
  }

  saveProject(results?: any, deleteScenarioResults = true) {
    if (this.project) {
      if (this.project.accountID === '') {
        this.project.accountID = localStorage.getItem('accountID');
      }
    }
    this.checkCurrenciesUsed();
    if (this.projectSaveToDatabaseDisabled) {
      this.projectSavedNotifier.next(true);
    } else {
      if (this.project._id === '') {
        this.saveNewProject(results, deleteScenarioResults);
      } else {
        this.saveExistingProject(deleteScenarioResults);
      }
    }
  }

  saveProjectAs(title: string) {
    const savedAsNotifier = new EventEmitter();
    this.projectNamesNotifier
      .pipe(takeUntil(savedAsNotifier)).subscribe((data) => {
        if (data) {
          if (this.checkUniqueProjectTitle(title)) {
            this.router.navigateByUrl('/home');
            this.toastrService.info(this.translationService.get('SERVICE.PROJECT_COMM.CREATING_PROJECT'));
            localStorage.removeItem('projectID');
            Utils.clearProjectPermissions();
            this.project.projectInfo.title = title;
            this.project._id = '';
            this.saveProject();
            this.unloadProject();
          }
        } else {
          this.getProjectNames();
        }
        savedAsNotifier.emit();
        savedAsNotifier.unsubscribe();
      });
  }

  unloadProject() {
    if (localStorage.getItem('projectID') !== null) {
      localStorage.removeItem('projectID');
    }
    Utils.clearProjectPermissions();
    this.projectDataLoaded = false;
    this.projectLoadedChange.next(false);
    this.projectLoadedValue = false;
    this.project = null;
    this.currentResultSet = 'latest';
    this.currentResults = null;
    this.resultNames = null;
    this.resultsLoadedNotifier.next(false);
    this.checkFullyLoaded();
  }

  /**
   *
   * @param id
   * @param targetPath where to navigate once loaded. Leave as empty string for no action
   */
  retrieveProjectFromDatabase(id?: string, targetPath?: string) {
    if (!targetPath) {
      targetPath = '';
    }
    this.nextPath = targetPath;
    this.projectDataLoaded = false;
    this.projectLoadedChange.next(false);
    this.projectLoadedValue = false;
    this.communicationService.getRequest(id, 'wsasapi/project/getproject')
      .pipe(take(1)).subscribe(
        data => {
          if (!data) return;
          const iProject: ProjectInterface = JSON.parse(JSON.stringify(data['obj']));
          this.project = new Project(iProject);
          // save project to local storage for page refreshes
          localStorage.setItem('projectID', this.project._id);
          if (data['newestVersion']) {
            localStorage.setItem('latestVersion', data['newestVersion']);
          }
          // Load and update user permissions
          this.communicationService.getAllRequest('/wsasapi/data/getUserPermissions?id=' + localStorage.getItem('userID'))
            .pipe(take(1))
            .subscribe(
              response => {
                const permissions: Permissions[] = response['obj'].map(permission => new Permissions(permission));
                // Only attempt to set plant permissions if a plant is loaded
                if (!this.project) {
                  if (!this.project) throw Error('project is not available in get user permissions request');
                }
                const projectPerms = permissions.find(p => p.projectId === this.project._id);
                Utils.setProjectPermissions(
                  projectPerms.canEdit,
                  projectPerms.canSimulate,
                  projectPerms.canDelete,
                  projectPerms.canEditInitialInformation,
                  projectPerms.readOnlyInitialChecklists,
                  projectPerms.editOnlyInitialChecklists
                );
                this.loadResults(targetPath);
                this.setProjectLoaded(true);
              }
            );
        }
      );
    if (!this.unitService.unitsLoadedNotifier.value) {
      this.unitService.loadData();
    }
  }

  reloadProject() {
    if (this.project._id) {
      this.communicationService.getRequest(this.project._id, 'wsasapi/project/getproject')
        .pipe(take(1)).subscribe(
          data => {
            const iProject: ProjectInterface = JSON.parse(JSON.stringify(data['obj']));
            if (data['newestVersion']) {
              localStorage.setItem('latestVersion', data['newestVersion']);
            }
            this.project = new Project(iProject);
            this.loadResults(undefined, true);
            this.projectReloadedNotifier.next(true);
          }
        );
    }
  }

  /**
   * this is done in this comm service to avoid circular dependencies
   */
  private removeEstimatedCapexItems() {
    let removed = false;
    if (this.project && this.project.financial) {
      const capex = this.project.financial.capex;
      if (capex && capex.length > 0) {
        for (let i = 0; i < capex.length; i++) {
          if (capex[i].estimated) {
            capex.splice(i, 1);
            i--;
            removed = true;
          }
        }
      }
    }
    return removed;
  }

  /**
   * this is done in this comm service to avoid circular dependencies
   */
  private removeEstimatedOpexItems() {
    let removed = false;
    if (this.project && this.project.financial) {
      const opex = this.project.financial.opex;
      if (opex && opex.length > 0) {
        for (let i = 0; i < opex.length; i++) {
          if (opex[i].estimated) {
            opex.splice(i, 1);
            i--;
            removed = true;
          }
        }
      }
    }
    return removed;
  }

  private saveNewProject(results?: any, deleteScenarioResults = true) {
    const preppedProject = this.prepProjectForSave(Project.deepCopy(this.project), deleteScenarioResults);
    (this.communicationService.postRequest(preppedProject, '/wsasapi/project') as Observable<{obj: ProjectInterface}>)
      .pipe(take(1)).subscribe(result => {
        if (!result) return;
        this.getProjectNames();
        this.communicationService.fetchReachedProjectLimit();
        const iProject = structuredClone(result.obj);
        this.project = new Project(iProject);
        // Import results if present
        if (results) {
          this.communicationService.postRequest(results, '/wsasapi/project/setResult/' + this.project._id)
            .pipe(take(1))
            .subscribe((data) => {
              this.project.scenarioComparison.resultHistoryID = data['obj']['_id'];
              this.loadResults();
              this.saveExistingProject(false);
            });
        }
        localStorage.setItem('projectID', this.project._id);
        Utils.setProjectPermissions(true, true, true, false, false, false);
        this.projectSavedNotifier.next(true);
        this.toastrService.success(this.translationService.get('SERVICE.PROJECT_COMM.NEW_PROJECT_SAVED'));
        this.setProjectLoaded(true);
        if (!this.unitService.unitsLoadedNotifier.value) {
          this.unitService.loadData();
        }
        if (!this.isProjectInput()) {

          if ((this.project?.projectInfo?.isDraftSubmission || this.project?.projectInfo?.isSubmittedProject) &&
          !this.project?.projectInfo?.isApprovedProject) {
            this.router.navigateByUrl('/project-input');
          } else {
            this.router.navigateByUrl('/project/projectinfo/general');
          }
        }
      });
  }

  private saveExistingProject(deleteScenarioResults = true, ) {
    const preppedProject = this.prepProjectForSave(Project.deepCopy(this.project), deleteScenarioResults);
    this.communicationService.postRequest(
      preppedProject, '/wsasapi/project/saveproject/' + this.project._id + '?ifu=' + Utils.isInitialFormUser()
    ).pipe(take(1)).subscribe(result => {
      if (result && result['obj']) {
        const iProject: ProjectInterface = JSON.parse(JSON.stringify(result['obj']));
        this.project = new Project(iProject);
        this.toastrService.success(this.translationService.get('SERVICE.PROJECT_COMM.PROJECT_SAVED'));
        this.projectSavedNotifier.next(true);
        if (!this.unitService.unitsLoadedNotifier.value) {
          this.unitService.loadData();
        }
      }
    });
  }

  private savePartialExistingProject(partialSavePackage) {
    this.communicationService.postRequest(
      {partialSavePackage: partialSavePackage}, '/wsasapi/project/savepartial/' + this.project._id + '?ifu=' + Utils.isInitialFormUser()
    ).pipe(take(1)).subscribe(result => {
      if (result && result['success']) { // true is all partial saves are successful
        this.toastrService.success(this.translationService.get('SERVICE.PROJECT_COMM.PROJECT_SAVED'));
        this.projectSavedNotifier.next(true);
      }
    });
  }

  private prepProjectForSave(project: Project, deleteScenarioResults: boolean) {
    if (project && project.plantSpecifications) {
      if (project.plantSpecifications.consumedFuels) {
        for (const fuel of project.plantSpecifications.consumedFuels) {
          if (!fuel._id) {
            delete fuel._id;
          }
          if (fuel.amount.unit === '') {
            fuel.amount.unit = null;
          }
        }
        for (const prod of project.plantSpecifications.generatedProducts) {
          if (!prod._id) {
            delete prod._id;
          }
        }
      }
    }
    delete project.results;
    if (deleteScenarioResults) {
      if (project?.scenarioComparison?.scenarios?.length > 0) {
        for (const scenario of project.scenarioComparison.scenarios) {
          if (scenario.results) {
            delete scenario.results;
          }
        }
      }
    }
    return project;
  }

  /**
   * Get any errors that were returned from the last simulation.
   */
  getResultErrors() {
    let result = null;
    const currentResults = this.getResults();
    if (currentResults) {
      if (Object.keys(currentResults).includes('RESULTS.DATA.ERRORS')) {
        result = currentResults['RESULTS.DATA.ERRORS'];
      }
    }
    return result;
  }


  sendListToService(sampleList: Alert[]) {
    this.sampleList = sampleList;
  }

  postToWarningsInfo() {
     this.communicationService.saveWarningsInfo(
      this.sampleList,
      this.project.accountID,
      this.getFullResultName(this.currentResultSet)
    );
  }

  postFromUnitValue(warning: string, time: Number) {
    this.communicationService.saveWarningsInfoFromUnitvalue(
      warning,
      this.project.accountID,
      this.getFullResultName(time + '')
    );
  }

  setScopingInfo(projectInfo) {
    if (this.getProject()._id === '') {
      // locally only
      this.setProjectInfo(projectInfo, null, true, true);
    } else {
      // save both checklists and scoping tests
      const partialSavePackage = [
        {updatedData: projectInfo.checklists, mongoPath: 'projectInfo.checklists'},
        {updatedData: projectInfo.scopingTests, mongoPath: 'projectInfo.scopingTests' }];
      // so we don't unintentionally override name of scoping projects
      this.savePartialExistingProject(partialSavePackage);
      // update project info
      this.project.projectInfo.checklists = projectInfo.checklists;
      this.project.projectInfo.scopingTests = projectInfo.scopingTests;
    }
  }
}
